import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Download, FileText, AlertCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import lmsApi from '../../services/api';
import PaymentModal from '../../components/PaymentModal';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateFormatter';
import { isWithinTimeWindow } from '../../utils/timeUtils';
import { toast } from 'sonner';

const LoanDetailsPage = () => {
    const { loanId } = useParams();
    const navigate = useNavigate();
    const [loan, setLoan] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [simState, setSimState] = useState<any>(null);
    const [selectedAction, setSelectedAction] = useState<'PAY' | 'CANCEL' | 'FORECLOSE'>('PAY');

    const totalDue = React.useMemo(() => {
        // PI Overdue = overdue_principal + overdue_interest from the latest DPD snapshot.
        // This exactly matches the "Overdue Total" in the DPD snapshot record (snap.overdue_amount).
        // Fees are excluded — this is strictly Principal + Interest overdue.
        if (simState !== null) {
            return (simState?.overdue_principal ?? 0) + (simState?.overdue_interest ?? 0);
        }
        // For non-SIM loans, fall back to serializer's total_overdue (also P+I from snapshot)
        if (loan?.total_overdue !== undefined) return parseFloat(String(loan.total_overdue));
        return 0;
    }, [loan?.total_overdue, simState]);

    const currentOutstanding = React.useMemo(() => {
        // Prefer simulation-specific state (matches "Net Cashout")
        if (simState?.pos !== undefined) return parseFloat(String(simState.pos));
        return parseFloat(String(loan?.current_outstanding || 0));
    }, [loan?.current_outstanding, simState]);

    const fetchLoanDetails = async () => {
        setLoading(true);
        try {
            const response = await lmsApi.post('/loans/');
            const foundLoan = response.data.find((l: any) => String(l.loan_application_number) === loanId || String(l.id) === loanId);

            if (foundLoan) {
                setLoan(foundLoan);
                if (loanId.startsWith('SIM_')) {
                    const stateRes = await lmsApi.post('/sim/state/', { loan_id: loanId });
                    setSimState(stateRes.data);
                }
            } else {
                setError('Loan not found');
            }
        } catch (err) {
            console.error(err);
            setError('Failed to fetch loan details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (loanId) {
            fetchLoanDetails();
        }
    }, [loanId]);

    const handleActionTrigger = async (action: string) => {
        if (!loanId) return;
        if (['PAY', 'CANCEL', 'FORECLOSE'].includes(action)) {
            setSelectedAction(action as any);
            setPaymentModalOpen(true);
        } else {
            toast.error(`${action} actions not implemented yet`);
        }
    };

    const getStatusColor = (status: string, currentTotalOverdue: number) => {
        if (currentTotalOverdue > 0) return 'bg-rose-500 hover:bg-rose-600';
        switch (status) {
            case 'ACTIVE':
            case 'DISBURSED': return 'bg-green-500 hover:bg-green-600';
            case 'CLOSED': return 'bg-gray-500 hover:bg-gray-600';
            case 'FORECLOSED': return 'bg-gray-700 hover:bg-gray-800';
            case 'DEFAULTED': return 'bg-red-500 hover:bg-red-600';
            case 'NPA': return 'bg-red-700 hover:bg-red-800';
            default: return 'bg-blue-500 hover:bg-blue-600';
        }
    };

    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'transaction_date', direction: 'desc' });

    const isPaymentWindowOpen = isWithinTimeWindow(0, 0, 22, 0); // Requirement 5: Disable after 10 PM

    const handleSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedTransactions = React.useMemo(() => {
        if (!loan?.transactions) return [];
        const sortableItems = [...loan.transactions];
        if (sortConfig.key) {
            sortableItems.sort((a: any, b: any) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Numeric sort for amount
                if (sortConfig.key === 'amount') {
                    aValue = parseFloat(aValue) || 0;
                    bValue = parseFloat(bValue) || 0;
                }

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [loan?.transactions, sortConfig]);

    if (loading) return <div className="p-8 text-center">Loading details...</div>;
    const isOverdue = simState?.is_overdue ?? loan?.is_overdue ?? false;

    if (loading) return <div className="p-8 text-center">Loading details...</div>;
    if (error || !loan) return <div className="p-8 text-center text-red-500">{error || 'Loan not found'}</div>;

    const documents = [
        { name: 'Loan Agreement', size: '2.4 MB' },
        { name: 'Sanction Letter', size: '1.1 MB' },
        { name: 'Repayment Schedule', size: '0.8 MB' },
    ];

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
            <div className="max-w-4xl mx-auto space-y-6">

                {/* Header / Nav */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate(-1)} className="p-0 hover:bg-transparent">
                        <ArrowLeft className="h-6 w-6 text-gray-600" />
                        <span className="ml-2 font-medium text-gray-600">Back to Dashboard</span>
                    </Button>
                </div>

                {/* Main Info Card */}
                <Card className="shadow-lg border-0 ring-1 ring-gray-200 bg-white">
                    <CardHeader className="bg-white border-b pb-4">
                        <div className="flex justify-between items-start">
                            <div>
                                <CardTitle className="text-2xl font-bold text-gray-900">Loan #{loan.loan_application_number}</CardTitle>
                                <CardDescription className="mt-1">
                                    Disbursed on {formatDate(loan.disbursement_date)}
                                </CardDescription>
                            </div>
                            <Badge className={`${getStatusColor(loan.status, isOverdue ? 1 : 0)} text-base px-3 py-1`}>
                                {isOverdue ? 'Overdue' : (['ACTIVE', 'DISBURSED'].includes(loan.status) ? 'On Time' : loan.status)}
                            </Badge>
                        </div>
                    </CardHeader>

                    <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-1">
                            <p className="text-sm text-gray-500 font-medium">Principle Outstanding</p>
                            <p className="text-3xl font-bold text-gray-900">₹{currentOutstanding.toFixed(2)}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm text-gray-500 font-medium">Sanctioned Amount</p>
                            <p className="text-xl font-semibold text-gray-900">₹{parseFloat(loan.loan_amount).toFixed(2)}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm text-gray-500 font-medium">Interest Rate</p>
                            <p className="text-xl font-semibold text-gray-900">{loan.interest_rate}% p.a.</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm text-gray-500 font-medium">Tenure</p>
                            <p className="text-lg text-gray-800">{loan.tenure_days} Days</p>
                            <p className="text-lg text-gray-800">{loan.tenure_months} {loan.tenure_unit || 'Months'}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm text-gray-500 font-medium">Lender</p>
                            {/* Assuming Lender data might not be fully expanded in list view, adding placeholder logic if missing */}
                            <p className="text-lg text-gray-800 font-medium">Intellend Capital</p>
                        </div>
                        {totalDue > 0 && (
                            <div className="space-y-1">
                                <p className={`text-sm ${isOverdue ? 'text-rose-500' : 'text-slate-500'} font-bold uppercase tracking-wider`}>PI Overdue</p>
                                <p className={`text-3xl font-black ${isOverdue ? 'text-rose-600' : 'text-slate-900'}`}>₹{totalDue.toFixed(2)}</p>
                            </div>
                        )}
                    </CardContent>

                    <CardFooter className="bg-gray-50 border-t p-4 flex flex-wrap gap-3 justify-end">
                        {(!simState?.has_demand && (!simState || simState.t_plus <= 3)) && (
                            <Button
                                variant="outline"
                                disabled={['CLOSED', 'FORECLOSED'].includes(loan.status) || !isPaymentWindowOpen}
                                onClick={() => handleActionTrigger('CANCEL')}
                                className="border-red-200 text-red-600 hover:bg-red-50"
                            >
                                Cancel Loan
                                {!isPaymentWindowOpen && " (Opens 12AM)"}
                            </Button>
                        )}
                        {(simState?.has_demand || (simState && simState.t_plus > 3)) && (
                            <Button
                                variant="outline"
                                disabled={['CLOSED', 'FORECLOSED'].includes(loan.status) || !isPaymentWindowOpen}
                                onClick={() => handleActionTrigger('FORECLOSE')}
                                className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                            >
                                Foreclose
                                {!isPaymentWindowOpen && " (Opens 12AM)"}
                            </Button>
                        )}
                        {/* Overdue pay button — SIM loans with demand */}
                        {(simState && (simState.has_demand || simState.t_plus > 3) && totalDue > 0) && (
                            <Button
                                size="lg"
                                disabled={!isPaymentWindowOpen}
                                onClick={() => handleActionTrigger('PAY')}
                                className="font-semibold shadow-sm bg-rose-600 hover:bg-rose-700 text-white"
                            >
                                Pay Total Due
                                {!isPaymentWindowOpen && " (Opens 12AM)"}
                            </Button>
                        )}
                        {/* Repayment button — all active/disbursed loans (real & SIM) */}
                        {['ACTIVE', 'DISBURSED'].includes(loan.status) && !(simState && (simState.has_demand || simState.t_plus > 3) && totalDue > 0) && (
                            <Button
                                size="lg"
                                disabled={!isPaymentWindowOpen}
                                onClick={() => handleActionTrigger('PAY')}
                                className="font-semibold shadow-sm bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                Make Repayment
                                {!isPaymentWindowOpen && " (Opens 12AM)"}
                            </Button>
                        )}
                    </CardFooter>
                </Card>

                {/* Documents Section */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Loan Documents</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {documents.map((doc, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors group cursor-pointer">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-600">
                                            <FileText size={20} />
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900">{doc.name}</p>
                                            <p className="text-xs text-gray-500">{doc.size}</p>
                                        </div>
                                    </div>
                                    <Button variant="ghost" size="icon" className="text-gray-400 group-hover:text-primary">
                                        <Download size={20} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Transaction History Section */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Transaction History</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loan.transactions && loan.transactions.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="cursor-pointer" onClick={() => handleSort('transaction_date')}>
                                            <div className="flex items-center gap-1">
                                                Date
                                                {sortConfig.key === 'transaction_date' && (
                                                    sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                                                )}
                                            </div>
                                        </TableHead>
                                        <TableHead className="cursor-pointer" onClick={() => handleSort('transaction_type')}>
                                            <div className="flex items-center gap-1">
                                                Type {sortConfig.key === 'transaction_type' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                                            </div>
                                        </TableHead>
                                        <TableHead className="cursor-pointer" onClick={() => handleSort('amount')}>
                                            <div className="flex items-center gap-1">
                                                Amount {sortConfig.key === 'amount' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                                            </div>
                                        </TableHead>
                                        <TableHead className="cursor-pointer" onClick={() => handleSort('status')}>
                                            <div className="flex items-center gap-1">
                                                Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                                            </div>
                                        </TableHead>
                                        <TableHead>Bill Type</TableHead>
                                        <TableHead>Reference</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedTransactions.map((txn: any) => (
                                        <TableRow key={txn.id}>
                                            <TableCell className="font-medium">
                                                {formatDate(txn.transaction_date)}
                                                <div className="text-xs text-gray-500">{formatTime(txn.transaction_date)}</div>
                                            </TableCell>
                                            <TableCell className={txn.transaction_type === 'CREDIT' ? 'text-green-600' : 'text-red-600'}>
                                                {txn.transaction_type}
                                            </TableCell>
                                            <TableCell>₹{parseFloat(txn.amount).toFixed(2)}</TableCell>
                                            <TableCell>
                                                {txn.status === 'SUCCESS' ? (
                                                    <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">Success</Badge>
                                                ) : txn.status === 'PENDING' ? (
                                                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">Pending</Badge>
                                                ) : (
                                                    <Badge className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100">{txn.status}</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {['INSTALLMENT', 'PAYMENT'].includes(txn.bill_type) ? (
                                                    <div className="space-y-1">
                                                        <Badge className={txn.bill_type === 'INSTALLMENT' ? "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100 font-medium" : "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 font-medium"}>
                                                            {txn.bill_type}
                                                        </Badge>
                                                        {(txn.split_principal > 0 || txn.split_interest > 0 || txn.split_fee > 0) && (
                                                            <div className="text-[10px] text-gray-600 space-y-0.5 mt-1">
                                                                {txn.split_principal > 0 && (
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                                                                        <span className="font-medium text-purple-700">Principal:</span>
                                                                        <span>₹{txn.split_principal.toFixed(2)}</span>
                                                                    </div>
                                                                )}
                                                                {txn.split_interest > 0 && (
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                                                                        <span className="font-medium text-orange-700">Interest:</span>
                                                                        <span>₹{txn.split_interest.toFixed(2)}</span>
                                                                    </div>
                                                                )}
                                                                {txn.split_fee > 0 && (
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                                        <span className="font-medium text-red-700">Fee:</span>
                                                                        <span>₹{txn.split_fee.toFixed(2)}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : txn.bill_type === 'FEE' ? (
                                                    <div className="space-y-1">
                                                        <Badge className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100 font-medium">
                                                            FEE
                                                        </Badge>
                                                        {txn.split_fee > 0 && (
                                                            <div className="text-[10px] text-gray-600">
                                                                <div className="flex items-center gap-1">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                                    <span className="font-medium text-red-700">Amount:</span>
                                                                    <span>₹{txn.split_fee.toFixed(2)}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : txn.bill_type === 'DISBURSEMENT' ? (
                                                    <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-100 font-medium">
                                                        DISBURSEMENT
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-gray-600">
                                                        {txn.bill_type || 'OTHER'}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground">{txn.description || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                                <AlertCircle size={32} className="mb-2 opacity-50" />
                                <p>No transactions recorded yet.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

            </div>

            <PaymentModal
                isOpen={paymentModalOpen}
                initialAction={selectedAction}
                onClose={() => setPaymentModalOpen(false)}
                loan={loan}
                onPaymentSuccess={() => {
                    setPaymentModalOpen(false);
                    // Ideally refetch details here
                    fetchLoanDetails();
                }}
            />

        </div >
    );
};

export default LoanDetailsPage;
